import {
  Asset,
  BatchUploadResponseSchema,
  CompleteMultipartUploadResponseSchema,
  InitiateMultipartUploadResponse,
  InitiateMultipartUploadResponseSchema,
  MultipartUploadPart,
  resolveFileType,
  SignMultipartUploadPartsResponseSchema,
  UploadResponseSchema,
  UploadResponse,
} from "@forgepoint/shared";
import { fetchApi } from "./api";

const ONE_MIB = 1024 * 1024;
const MULTIPART_UPLOAD_THRESHOLD_BYTES = 100 * ONE_MIB;
const MULTIPART_UPLOAD_CONCURRENCY = 4;
const MAX_PART_UPLOAD_ATTEMPTS = 3;
const PART_RETRY_DELAY_MS = 1_000;
const MAX_CONCURRENT_UPLOADS = 20;
let activeTransfers = 0;
const waitingTransfers: Array<() => void> = [];

const UPLOAD_URL_BATCH_SIZE = 200;

/** Why a browser-to-storage transfer failed. */
export enum UploadTransferErrorKind {
  NETWORK = "network",
  HTTP = "http",
  MISSING_ETAG = "missing_etag",
}

/** Thrown when the browser fails to PUT a file or a part to storage. */
export class UploadTransferError extends Error {
  readonly kind: UploadTransferErrorKind;
  readonly status: number;
  readonly bytesSent: number;
  readonly elapsedMs: number;

  constructor(
    message: string,
    details: {
      kind: UploadTransferErrorKind;
      status: number;
      bytesSent: number;
      elapsedMs: number;
    },
  ) {
    super(message);
    this.name = "UploadTransferError";
    this.kind = details.kind;
    this.status = details.status;
    this.bytesSent = details.bytesSent;
    this.elapsedMs = details.elapsedMs;
  }
}

export interface UploadAssetProgress {
  loadedBytes: number;
  totalBytes: number;
  percent: number;
}

export interface UploadAssetOptions {
  onProgress?: (progress: UploadAssetProgress) => void;
  signal?: AbortSignal;
}

/** Callbacks for per-file progress and completed transfers in a bulk upload. */
export interface UploadAssetsOptions {
  onProgress?: (index: number, progress: UploadAssetProgress) => void;
  onUploaded?: (index: number, asset: Asset) => void;
}

type PreparedUploadBatch = PromiseSettledResult<Map<number, UploadResponse>>;

/** Continuously refills upload workers, prefetching one URL batch ahead. */
export async function uploadAssets(
  files: File[],
  options: UploadAssetsOptions = {},
): Promise<PromiseSettledResult<Asset>[]> {
  const preparedBatches = new Map<number, Promise<PreparedUploadBatch>>();
  const prepareBatch = (batchIndex: number) => {
    let batch = preparedBatches.get(batchIndex);
    if (!batch) {
      const offset = batchIndex * UPLOAD_URL_BATCH_SIZE;
      batch = prepareUploadBatch(
        files.slice(offset, offset + UPLOAD_URL_BATCH_SIZE),
        offset,
      );
      preparedBatches.set(batchIndex, batch);
    }
    return batch;
  };

  const results: PromiseSettledResult<Asset>[] = [];
  let nextIndex = 0;
  await Promise.all(
    Array.from(
      { length: Math.min(MAX_CONCURRENT_UPLOADS, files.length) },
      async () => {
        while (nextIndex < files.length) {
          const index = nextIndex++;
          const batchIndex = Math.floor(index / UPLOAD_URL_BATCH_SIZE);
          if (index % UPLOAD_URL_BATCH_SIZE === 0) {
            preparedBatches.delete(batchIndex - 1);
            prepareBatch(batchIndex);
            if ((batchIndex + 1) * UPLOAD_URL_BATCH_SIZE < files.length) {
              prepareBatch(batchIndex + 1);
            }
          }
          const file = files[index];
          const uploadOptions = {
            onProgress: (progress: UploadAssetProgress) =>
              options.onProgress?.(index, progress),
          };
          try {
            let asset: Asset;
            if (file.size >= MULTIPART_UPLOAD_THRESHOLD_BYTES) {
              asset = await uploadAsset(file, uploadOptions);
            } else {
              const prepared = await prepareBatch(batchIndex);
              if (prepared.status === "rejected") {
                throw prepared.reason;
              }
              const upload = prepared.value.get(index);
              if (!upload) {
                throw new Error("Upload URL batch is missing a file");
              }
              asset = await uploadAssetSimple(file, uploadOptions, upload);
            }
            results[index] = { status: "fulfilled", value: asset };
            options.onUploaded?.(index, asset);
          } catch (reason) {
            results[index] = { status: "rejected", reason };
          }
        }
      },
    ),
  );
  return results;
}

async function prepareUploadBatch(
  files: File[],
  offset: number,
): Promise<PreparedUploadBatch> {
  const smallFiles = files.flatMap((file, index) => {
    if (file.size >= MULTIPART_UPLOAD_THRESHOLD_BYTES) {
      return [];
    }
    return [{ file, index: offset + index }];
  });
  const preparedUploads = new Map<number, UploadResponse>();
  try {
    if (smallFiles.length > 0) {
      const response = await fetchApi("api/assets/upload/batch", "POST", {
        body: {
          files: smallFiles.map(({ file }) => ({
            fileName: file.name,
            fileType: resolveFileType(file.name, file.type),
            fileSize: file.size,
          })),
        },
      });
      if (!response.ok) {
        throw new Error(response.error ?? "Failed to create uploads");
      }
      const { results } = BatchUploadResponseSchema.parse(response.data);
      if (results.length !== smallFiles.length) {
        throw new Error("Upload URL count does not match file count");
      }
      smallFiles.forEach(({ index }, position) =>
        preparedUploads.set(index, results[position]),
      );
    }
    return { status: "fulfilled", value: preparedUploads };
  } catch (reason) {
    return { status: "rejected", reason };
  }
}

interface UploadBlobToUrlOptions {
  uploadUrl: string;
  blob: Blob;
  contentType?: string;
  requireEtag?: boolean;
  signal?: AbortSignal;
  onProgress?: (loadedBytes: number) => void;
}

export async function uploadAsset(
  file: File,
  options: UploadAssetOptions = {},
): Promise<Asset> {
  if (file.size >= MULTIPART_UPLOAD_THRESHOLD_BYTES) {
    return uploadAssetMultipart(file, options);
  }

  return uploadAssetSimple(file, options);
}

/** Deletes an uploaded asset, e.g. one the vault did not register. */
export async function deleteAsset(assetId: string): Promise<boolean> {
  const response = await fetchApi(`api/assets/${assetId}`, "DELETE");
  return response.ok;
}

async function uploadAssetSimple(
  file: File,
  options: UploadAssetOptions,
  preparedUpload?: UploadResponse,
): Promise<Asset> {
  const fileType = resolveFileType(file.name, file.type);
  let upload = preparedUpload;
  if (!upload) {
    const response = await fetchApi("api/assets/upload", "POST", {
      body: {
        fileName: file.name,
        fileType,
        fileSize: file.size,
      },
    });

    if (!response.ok) {
      throw new Error(
        response.error ?? `Failed to create upload for ${file.name}`,
      );
    }

    upload = UploadResponseSchema.parse(response.data);
  }
  const { uploadUrl, asset } = upload;
  await uploadBlobToSignedUrl({
    uploadUrl,
    blob: file,
    contentType: fileType,
    signal: options.signal,
    onProgress: (loadedBytes) =>
      reportProgress(options.onProgress, loadedBytes, file.size),
  });
  reportProgress(options.onProgress, file.size, file.size);

  return asset;
}

async function uploadAssetMultipart(
  file: File,
  options: UploadAssetOptions,
): Promise<Asset> {
  const initiatedUpload = await initiateMultipartUpload(file);
  const partCount = Math.ceil(file.size / initiatedUpload.partSize);
  const partNumbers = Array.from(
    { length: partCount },
    (_, index) => index + 1,
  );
  const uploadedParts = new Map<number, MultipartUploadPart>();
  const partProgress = new Map<number, number>();
  let nextPartIndex = 0;

  const reportMultipartProgress = () => {
    const loadedBytes = Array.from(partProgress.values()).reduce(
      (total, bytes) => total + bytes,
      0,
    );
    reportProgress(options.onProgress, loadedBytes, file.size);
  };

  try {
    await Promise.all(
      Array.from({
        length: Math.min(MULTIPART_UPLOAD_CONCURRENCY, partNumbers.length),
      }).map(async () => {
        while (nextPartIndex < partNumbers.length) {
          const partNumber = partNumbers[nextPartIndex];
          nextPartIndex += 1;
          const part = await uploadMultipartPartWithRetries({
            file,
            initiatedUpload,
            partNumber,
            signal: options.signal,
            onProgress: (loadedBytes) => {
              partProgress.set(partNumber, loadedBytes);
              reportMultipartProgress();
            },
          });

          uploadedParts.set(partNumber, part);
        }
      }),
    );

    const completedParts = partNumbers.map((partNumber) => {
      const part = uploadedParts.get(partNumber);
      if (!part) {
        throw new Error(`Multipart upload missing part ${partNumber}`);
      }
      return part;
    });

    const response = await fetchApi(
      "api/assets/upload/multipart/complete",
      "POST",
      {
        body: {
          uploadToken: initiatedUpload.uploadToken,
          parts: completedParts,
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        response.error ?? `Failed to complete upload for ${file.name}`,
      );
    }

    const { asset } = CompleteMultipartUploadResponseSchema.parse(
      response.data,
    );
    reportProgress(options.onProgress, file.size, file.size);

    return asset;
  } catch (error) {
    await abortMultipartUpload(initiatedUpload.uploadToken);
    throw error;
  }
}

async function initiateMultipartUpload(
  file: File,
): Promise<InitiateMultipartUploadResponse> {
  const response = await fetchApi(
    "api/assets/upload/multipart/initiate",
    "POST",
    {
      body: {
        fileName: file.name,
        fileType: resolveFileType(file.name, file.type),
        fileSize: file.size,
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      response.error ?? `Failed to create upload for ${file.name}`,
    );
  }

  return InitiateMultipartUploadResponseSchema.parse(response.data);
}

async function uploadMultipartPartWithRetries(input: {
  file: File;
  initiatedUpload: InitiateMultipartUploadResponse;
  partNumber: number;
  signal?: AbortSignal;
  onProgress: (loadedBytes: number) => void;
}): Promise<MultipartUploadPart> {
  for (let attempt = 1; attempt <= MAX_PART_UPLOAD_ATTEMPTS; attempt += 1) {
    try {
      input.onProgress(0);
      return await uploadMultipartPart(input);
    } catch (error) {
      if (
        input.signal?.aborted ||
        attempt === MAX_PART_UPLOAD_ATTEMPTS ||
        !isRetryableUploadError(error)
      ) {
        throw error;
      }
      await sleep(PART_RETRY_DELAY_MS * attempt);
    }
  }

  throw new Error(`Failed to upload part ${input.partNumber}`);
}

/** Retries everything except a storage response that will not change, e.g. a 403. */
function isRetryableUploadError(error: unknown): boolean {
  if (!(error instanceof UploadTransferError)) {
    return true;
  }
  if (error.kind === UploadTransferErrorKind.NETWORK) {
    return true;
  }
  return (
    error.kind === UploadTransferErrorKind.HTTP &&
    (error.status >= 500 || error.status === 429 || error.status === 408)
  );
}

async function uploadMultipartPart(input: {
  file: File;
  initiatedUpload: InitiateMultipartUploadResponse;
  partNumber: number;
  signal?: AbortSignal;
  onProgress: (loadedBytes: number) => void;
}): Promise<MultipartUploadPart> {
  const response = await fetchApi("api/assets/upload/multipart/parts", "POST", {
    body: {
      uploadToken: input.initiatedUpload.uploadToken,
      partNumbers: [input.partNumber],
    },
  });

  if (!response.ok) {
    throw new Error(
      response.error ?? `Failed to sign part ${input.partNumber}`,
    );
  }

  const signedParts = SignMultipartUploadPartsResponseSchema.parse(
    response.data,
  );
  const signedPart = signedParts.parts[0];
  if (!signedPart) {
    throw new Error(`Missing signed URL for part ${input.partNumber}`);
  }

  const startByte = (input.partNumber - 1) * input.initiatedUpload.partSize;
  const endByte = Math.min(
    input.partNumber * input.initiatedUpload.partSize,
    input.file.size,
  );
  const etag = await uploadBlobToSignedUrl({
    uploadUrl: signedPart.uploadUrl,
    blob: input.file.slice(startByte, endByte),
    requireEtag: true,
    signal: input.signal,
    onProgress: input.onProgress,
  });

  return {
    partNumber: input.partNumber,
    etag,
  };
}

async function abortMultipartUpload(uploadToken: string): Promise<void> {
  try {
    await fetchApi("api/assets/upload/multipart/abort", "POST", {
      body: { uploadToken },
    });
  } catch {
    // Best-effort cleanup. S3 lifecycle policy should clean incomplete uploads too.
  }
}

async function uploadBlobToSignedUrl(
  input: UploadBlobToUrlOptions,
): Promise<string> {
  if (activeTransfers >= MAX_CONCURRENT_UPLOADS) {
    await new Promise<void>((resolve) => waitingTransfers.push(resolve));
  } else {
    activeTransfers++;
  }
  try {
    return await performBlobUpload(input);
  } finally {
    const next = waitingTransfers.shift();
    if (next) {
      next();
    } else {
      activeTransfers--;
    }
  }
}

function performBlobUpload({
  uploadUrl,
  blob,
  contentType,
  requireEtag,
  signal,
  onProgress,
}: UploadBlobToUrlOptions): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const startedAt = Date.now();
    let bytesSent = 0;
    const transferError = (kind: UploadTransferErrorKind, message: string) =>
      new UploadTransferError(message, {
        kind,
        status: xhr.status,
        bytesSent,
        elapsedMs: Date.now() - startedAt,
      });

    const cleanup = () => {
      signal?.removeEventListener("abort", handleAbort);
    };
    const handleAbort = () => {
      cleanup();
      xhr.abort();
      reject(new DOMException("Upload aborted", "AbortError"));
    };

    if (signal?.aborted) {
      handleAbort();
      return;
    }

    signal?.addEventListener("abort", handleAbort, { once: true });
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        bytesSent = event.loaded;
        onProgress?.(event.loaded);
      }
    };
    xhr.onload = () => {
      cleanup();

      if (xhr.status < 200 || xhr.status >= 300) {
        reject(
          transferError(
            UploadTransferErrorKind.HTTP,
            `Upload failed with status ${xhr.status}`,
          ),
        );
        return;
      }

      const etag = xhr.getResponseHeader("ETag");
      if (requireEtag && !etag) {
        reject(
          transferError(
            UploadTransferErrorKind.MISSING_ETAG,
            "Upload response did not include an ETag header",
          ),
        );
        return;
      }

      resolve(etag ?? "");
    };
    xhr.onerror = () => {
      cleanup();
      reject(
        transferError(
          UploadTransferErrorKind.NETWORK,
          "Upload failed: network error",
        ),
      );
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Upload aborted", "AbortError"));
    };
    xhr.open("PUT", uploadUrl);
    if (contentType) {
      xhr.setRequestHeader("Content-Type", contentType);
    }
    xhr.send(blob);
  });
}

function reportProgress(
  onProgress: UploadAssetOptions["onProgress"],
  loadedBytes: number,
  totalBytes: number,
) {
  onProgress?.({
    loadedBytes,
    totalBytes,
    percent:
      totalBytes > 0 ? Math.round((loadedBytes / totalBytes) * 100) : 100,
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
